import { FormArray, FormGroup } from '@angular/forms';
import { ICON_COLOR_OPTIONS } from 'app/shared/constants/lists.constant';
import { FieldGroup } from './field-group.interface';

/** Convierte colores guardados por nombre ("blue") al hexadecimal de la paleta. */
export function normalizeIconColor(raw: unknown): string | null {
  const value = (raw ?? '').toString().trim();
  if (!value) return null;
  const preset = ICON_COLOR_OPTIONS.find(
    (option) => option.name.toLowerCase() === value.toLowerCase()
  );
  return preset ? preset.value : value.toUpperCase();
}

/** Primer entero >= 1 que no está en la lista (1,2,5 -> 3). */
export function firstFreePosition(taken: number[]): number {
  const used = new Set(taken.map((value) => Number(value) || 0));
  let position = 1;
  while (used.has(position)) position++;
  return position;
}

/** Primer hueco libre dentro del grupo del campo dado. */
export function nextFreePosition(
  fieldGroup: FormGroup,
  fieldsArray: FormArray,
  groupId: string | null
): number {
  return firstFreePosition(
    fieldsArray.controls
      .filter((ctrl) => ctrl !== fieldGroup && (ctrl.get('groupId')?.value ?? null) === groupId)
      .map((ctrl) => Number(ctrl.get('position')?.value) || 0)
  );
}

/** Asigna al campo la primera posición libre de su grupo. */
export function fillNextFreePosition(fieldGroup: FormGroup, groupId: string | null): void {
  const formArray = fieldGroup.parent as FormArray;
  if (!formArray) return;
  fieldGroup.get('position')?.setValue(nextFreePosition(fieldGroup, formArray, groupId ?? null));
}

/**
 * Datos heredados: el primer campo de cada grupo se guardaba en 0. Con el mínimo en 1 eso
 * bloquearía todo el formulario, así que a los campos < 1 se les asigna un hueco libre.
 */
export function normalizeFieldPositions(fieldsArray: FormArray): void {
  fieldsArray.controls.forEach((ctrl) => {
    const fieldGroup = ctrl as FormGroup;
    if (Number(fieldGroup.get('position')?.value) >= 1) return;
    fieldGroup
      .get('position')
      ?.setValue(
        nextFreePosition(fieldGroup, fieldsArray, fieldGroup.get('groupId')?.value ?? null),
        { emitEvent: false }
      );
  });
}

/** Grupos heredados con posición < 1 reciben el primer hueco libre. */
export function normalizeGroupPositions(groups: FieldGroup[]): FieldGroup[] {
  const taken = groups.map((group) => group.position).filter((position) => position >= 1);
  return groups.map((group) => {
    if (group.position >= 1) return group;
    const position = firstFreePosition(taken);
    taken.push(position);
    return { ...group, position };
  });
}
